import Button from 'components/actions/Button';
import RadioButton from 'components/forms/RadioButton';
import type { Position } from 'components/map/types';
import Tooltip from 'components/overlays/Tooltip';
import { useMemo, useState } from 'react';
import { keys, mapValues } from 'remeda';

import type { HereRoute } from './hereRouting';
import { fitToSimulation, simplifyToMax } from './interpolation';

type ExportPanelProps = {
    positions: Position[];
    route: HereRoute | undefined;
};

const MAX_SIMPLIFIED_POINTS = 500;
const PREVIEW_CHAR_LIMIT = 2000;

const simplify = (positions: Position[]) => {
    const kept = simplifyToMax(positions, MAX_SIMPLIFIED_POINTS);
    return positions.filter((_, i) => kept.has(i));
};

const SOURCES = {
    raw: {
        label: 'Waypoints',
        description: 'Exactly what you entered: Pasted or manually added points.',
        needsRoute: false,
        resolve: (positions: Position[]) => positions,
    },
    'rdp-simplified': {
        label: 'Simplified',
        description:
            'A compact route definition. Corners and turns kept, straight stretches compressed. Uses the HERE polyline when a route has been calculated, otherwise your raw waypoints.',
        needsRoute: false,
        resolve: (positions: Position[], route?: HereRoute) => simplify(route?.positions ?? positions),
    },
    'here-polyline': {
        label: 'HERE polyline',
        description:
            'The route as calculated by HERE, following actual road geometry. Very dense, one point every few meters.',
        needsRoute: true,
        resolve: (_: Position[], route?: HereRoute) => route?.positions ?? [],
    },
    'fit-to-simulation': {
        label: 'Fit to simulation',
        description:
            'The HERE route with evenly-spaced points, one per second of drive time. Use this to drive a simulation at the correct speed.',
        needsRoute: true,
        resolve: (_: Position[], route?: HereRoute) =>
            route ? fitToSimulation(route.positions, route.distanceMeters / route.durationSeconds) : [],
    },
};

const FORMATS = {
    'raw-tuple': {
        label: 'Raw tuples',
        filename: 'route-builder-raw.json',
        toItem: (p: Position) => [p.lat, p.lng],
    },
    scenario: {
        label: 'Scenario',
        filename: 'route-builder-scenario.json',
        toItem: (p: Position) => ({ position: [p.lat, p.lng] }),
    },
    'predefined-routes': {
        label: 'Predefined routes',
        filename: 'route-builder-predefined.json',
        toItem: (p: Position) => ({ latitude: p.lat, longitude: p.lng }),
    },
};

type Source = keyof typeof SOURCES;
type Format = keyof typeof FORMATS;

const downloadJson = (content: string, filename: string) => {
    const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

const ExportPanel = ({ positions, route }: ExportPanelProps) => {
    const [source, setSource] = useState<Source>('raw');
    const [format, setFormat] = useState<Format>('raw-tuple');
    const [copied, setCopied] = useState(false);

    const isAvailable = (value: Source) => !SOURCES[value].needsRoute || route !== undefined;
    // Falls back to the raw waypoints when the selected source needs a route that no longer exists
    const selectedSource = isAvailable(source) ? source : 'raw';

    const resolved = useMemo(() => mapValues(SOURCES, s => s.resolve(positions, route)), [positions, route]);

    const exported = resolved[selectedSource];
    const toItem = FORMATS[format].toItem as (p: Position) => unknown;
    const output = exported.length > 0 ? JSON.stringify(exported.map(toItem), null, 2) : '';
    const preview = output.length > PREVIEW_CHAR_LIMIT ? `${output.slice(0, PREVIEW_CHAR_LIMIT)}\n…` : output;

    const handleCopy = async () => {
        await navigator.clipboard.writeText(output);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className='RouteBuilderExport'>
            <div className='RouteBuilderExport__group'>
                <div className='RouteBuilderExport__label'>Source</div>
                {keys(SOURCES).map(value => (
                    <RadioButton
                        key={value}
                        name='rb-source'
                        checked={selectedSource === value}
                        disabled={!isAvailable(value)}
                        onChange={() => setSource(value)}
                    >
                        {SOURCES[value].label}
                        {resolved[value].length > 0 && ` (${resolved[value].length})`}
                        <Tooltip
                            placement='right'
                            width={250}
                            trigger={
                                <span className='rioglyph rioglyph-info-sign text-color-gray text-12 margin-left-4' />
                            }
                        >
                            <Tooltip.Content>{SOURCES[value].description}</Tooltip.Content>
                        </Tooltip>
                    </RadioButton>
                ))}
            </div>

            <div className='RouteBuilderExport__group'>
                <div className='RouteBuilderExport__label'>Format</div>
                {keys(FORMATS).map(value => (
                    <RadioButton
                        key={value}
                        name='rb-format'
                        checked={format === value}
                        onChange={() => setFormat(value)}
                    >
                        {FORMATS[value].label}
                    </RadioButton>
                ))}
            </div>

            {output ? (
                <>
                    <div className='RouteBuilderExport__meta'>
                        {exported.length} point{exported.length !== 1 ? 's' : ''}
                    </div>
                    <textarea
                        className='RouteBuilderExport__preview form-control text-12'
                        readOnly
                        rows={6}
                        value={preview}
                    />
                </>
            ) : (
                <p className='RouteBuilderExport__empty'>No points to export.</p>
            )}

            <div className='RouteBuilderExport__actions'>
                <Button
                    size='sm'
                    className='flex-1'
                    disabled={!output}
                    onClick={() => downloadJson(output, FORMATS[format].filename)}
                >
                    <span className='rioglyph rioglyph-download margin-right-5' />
                    Download
                </Button>
                <Button size='sm' className='flex-1' disabled={!output} onClick={handleCopy}>
                    <span className={`rioglyph rioglyph-${copied ? 'ok' : 'copy'} margin-right-5`} />
                    {copied ? 'Copied!' : 'Copy'}
                </Button>
            </div>
        </div>
    );
};

export default ExportPanel;
